import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
//import emailjs from "@emailjs/browser";
import { Contact } from "lucide-react";

export const FAQPage = () => {
  const navigate = useNavigate();
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const BASEURL = process.env.REACT_APP_BASEURL;

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    console.log("Demo request:", formData);
    try {
      const res = await fetch(`${BASEURL}/api/WebsiteEmailApi/SendContact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Name:formData.name,
          Phone:formData.phone,
          Email:formData.email,
          Company:formData.company,
          Message:formData.message
        })
      });
      if (res.status) {
        toast.success("Demo request submitted successfully! We'll contact you soon.");
        setDemoDialogOpen(false);
        setFormData({ name: "", email: "", phone: "", company: "", message: "" });
      }
    } catch (error) {
      console.log(error.stack);
      toast.warning("Error booking a demo. Please try again");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center">
              <a href="/" data-testid="logo">
                <img
                  // src="https://customer-assets.emergentagent.com/job_fintech-demo-9/artifacts/hrowc68u_image.png"
                  src = "/images/turbopayUpdatedBig.jpeg"
                  alt="TurboPay"
                  className="h-8 md:h-14 w-auto"
                />
              </a>
            </div>
            <div className="hidden lg:flex items-center space-x-8 text-lg">
              <a href="/" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors">Home</a>
              {/* <a href="/pricing" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors">Pricing</a> */}
              <Link to="/pricing" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-pricing">
                Pricing
              </Link>
              {/* <a href="/faq" className="text-[#1F497D] hover:text-[#183a64] font-medium transition-colors">FAQs</a> */}
              <Link to="/faq" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-faq">
                FAQs
              </Link>
            </div>
            <div className="hidden lg:block">
              <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1F497D] text-white hover:bg-[#183a64] h-11 px-8 rounded-full font-semibold shadow-[0_4px_14px_0_rgba(31,73,125,0.3)] transition-all hover:scale-105 active:scale-95">
                    Get Demo
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle className="font-outfit text-2xl">Book a Demo</DialogTitle>
                    <DialogDescription>
                      Fill out the form below and we'll get back to you shortly.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleDemoSubmit} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input id="name" name="name" required value={formData.name} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input id="company" name="company" value={formData.company} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea id="message" name="message" rows={3} value={formData.message} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <Button type="submit" className="w-full bg-[#1F497D] hover:bg-[#183a64] h-11 rounded-full font-semibold">
                      Book Demo
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </nav>

      {/* FAQ Section */}
      <section className="py-12  md:py-8" data-testid="faq-section">
        <div className="container-custom">
          <h1 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-center mb-4 text-slate-900">
            Frequently Asked Questions
          </h1>
          <p className="text-center text-lg text-slate-600 mb-12 max-w-2xl mx-auto">
            Find answers to common questions about TurboPay's collections software and funding services
          </p>

          <div className="max-w-4xl mx-auto space-y-12 text-base md:text-lg">
            {/* General FAQs */}
            <div>
              <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-[#1F497D]">General</h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="general-1" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Do You Offer Free Trial?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base">
                    Yes, TurboPay's free trial lets you explore its powerful features for 15 days at no cost. Currently, the trial is available to Tally ERP users and those willing to upload receivables data via Excel. Please email us your details on sales@TurboPay.in
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="general-2" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Which ERP and Accounting Softwares do you support?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    At present, TurboPay is integrated with Tally.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="general-3" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Do you have a smartphone app?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes, TurboPay's smart mobile app empowers sales teams to collect payments faster and more efficiently.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="general-4" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Is my data secure?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    We adopt industry appropriate data collection, storage and processing practices, and security measures to protect users' personal Information. These security measures include password protected directories and databases, encryption of key fields in the database, SSL (Secure Sockets Layered) technology, and IP based access restrictions. Our servers are hosted on AWS servers and backed up daily.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="general-5" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    How do I know if TurboPay is right for my business?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay is right for all business that sell on credit. Please refer our Benefits section to see how TurboPay can help your business.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Collections FAQs */}
            {/* <div>
              <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-200">Collections</h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="collections-1" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Does TurboPay support multi-currency?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes, if your ERP or accounting software supports multi-currency, the same gets reflected in TurboPay.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-2" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    What is the "from" address of emails sent out from TurboPay to my customers?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    You can decide from which email ID you want to send emails to your customers. You can configure TurboPay to send emails from your Gmail, or your business email ID.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-3" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Can I login from web, smart phone, and smart tab with one user license?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes. A user can login to TurboPay from web, smart phone or tab with one user license.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-4" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    How will I learn to use the software?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay is very user friendly. We offer product training at the time of implementation. Further, we also provide on-demand support through phone, email and audio / video conference calls. For any specific support requirements, please email us on support@TurboPay.com.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-5" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    How long is the implementation process?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay is quick and easy to implement—just a few hours for Tally ERP users or businesses uploading receivables via Excel. We provide instant training so your team can start using it right away. Most customers fully adopt TurboPay within days and see measurable benefits in just one week.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-6" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Does TurboPay have customer support?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes, we provide support via telephone, email and audio / video conference calls to all our customers.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-7" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Do you writeback any data into our ERP or accounting software?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    No. We only read data and do not write back any data into your ERP or accounting software.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div> */}

            {/* Distributor Funding FAQs */}
            <div>
              <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-green-200">Distributor</h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="dist-funding-1" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q1: Why should I adopt TurboPay? I already have a collection process. 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Most distributors lose 3–5% of revenue to cash misappropriation by field 
                    sales teams — cash collected but not deposited, or deposited late with no visibility. TurboPay eliminates this by recording every collection digitally at the point of receipt, with OTP confirmation from the retailer and geo-verification of the field agent. You get complete visibility into every rupee collected, the moment it is collected.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="dist-funding-2" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q2: Does my field team need training to use TurboPay? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    The app is designed for field agents with minimal digital literacy. 
                    Simple interface, OTP confirmation, geo-check — everything done in under 30 seconds at the point
                     of collection. Most field teams are fully operational within one day of onboarding.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="dist-funding-3" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q3: Does TurboPay replace my existing accounting software? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    No. TurboPay sits alongside your existing accounting or ERP system. It digitises the collections layer and feeds reconciled data back into Tally or your Excel records.
                     We do not replace your existing tools — we make them more accurate.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="dist-funding-4" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q4: How does TurboPay help me get working capital?  
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    As you use TurboPay for collections, every invoice and repayment builds your trade signal profile. NBFCs connected to TurboPay can offer you working capital based on this live profile — credit
                     limits that grow as your trade grows, without annual reviews or financial statement submissions.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="dist-funding-5" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q5: Do I need collateral or financial statements to access credit? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    No. Credit offered through TurboPay's platform is based on your trade behaviour — invoice history, repayment frequency and cycle patterns. This is particularly valuable for distributors
                     who are creditworthy based on their trade but underserved by traditional balance-sheet lending.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="dist-funding-6" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q6: What does TurboPay cost? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay charges a flat monthly platform fee per distributor node — no setup fees, no per-transaction 
                    charges, no hidden costs. Contact sales@turbopay.cash for current pricing.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="dist-funding-7" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q7: How quickly can I go live? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Most distributors are fully live within 48 hours of signing up — 
                    invoices uploaded, field team onboarded, collections recording from day one.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="dist-funding-8" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q8: Is my data secure? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes. TurboPay uses SSL encryption, password-protected databases with encrypted key fields, IP-based 
                    access restrictions and AWS-hosted servers with daily backups. You own your data and can export it at any time.
                  </AccordionContent>
                </AccordionItem>

              </Accordion>
            </div>

            {/* Retailer Funding FAQs */}
            <div>
              <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-orange-200">NBFCs & Banks </h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="ret-funding-1" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q1: What does TurboPay offer to NBFCs and banks? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay is a credit infrastructure platform that gives institutional lenders invoice-level repayment behaviour captured continuously across a live distributor network. You connect via API and deploy credit against real-time trade signals.
                     Collections flow back directly into your bank account through TurboPay's UPI rails — no manual collections effort required.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ret-funding-2" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q2: What signals does TurboPay provide for underwriting? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay captures repayment timing, collection cycle frequency, invoice-level payment behaviour, retailer identity — GST and mobile verified — and outstanding exposure. All signals are
                     updated in real time across every distributor node and accessible via API or through TurboPay's lender dashboard.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ret-funding-3" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q3: Does TurboPay lend money? What is TurboPay's role in the credit transaction? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay is pure infrastructure — we do not lend. Capital belongs entirely to the NBFC or bank. TurboPay provides the signal layer, disbursement rails, collections mechanism and repayment routing. We earn a small infra take-rate
                     on AUM deployed through the platform — we have zero balance sheet exposure and no competing interest in your book.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ret-funding-4" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q4: How do we integrate with TurboPay? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Integration is via API. TurboPay provides complete API documentation, a sandbox environment and a dedicated integration team.
                     Most lenders are live on the platform within 2–4 weeks of signing the partnership agreement.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ret-funding-5" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q5: Can we pilot before committing to a full partnership? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes. TurboPay actively encourages a pilot first. We have 10 live distributor nodes and 15,000 retailers transacting on the network today — you 
                    can deploy pilot capital against a live, verified network immediately without waiting for new distributor onboarding.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ret-funding-6" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q6: How does TurboPay handle regulatory compliance for co-lending? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay operates with a regulatory-aligned structure for institutional co-lending designed to comply with RBI guidelines for NBFC and 
                    bank partnerships. Full documentation is available under NDA. Contact raj@turbopay.cash to initiate discussions.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ret-funding-7" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                   Q7: Is TurboPay live with any banking partners today? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes. TurboPay's UPI collection rails are live with IDFC First Bank. We are currently onboarding 
                    our first NBFC lending partner. If you are interested in a pilot, contact raj@turbopay.cash.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ret-funding-8" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Q8: What is the minimum network size required to start a pilot? 
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    We recommend a minimum of 5–10 distributor nodes for a pilot — enough signal density to demonstrate underwriting value. Our current live network of 10 
                    distributor nodes and 15,000 verified retailers is available for pilot deployment immediately.
                  </AccordionContent>
                </AccordionItem>

                {/* <AccordionItem value="ret-funding-5" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Can I see a free online demo?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes. we will be delighted to give you a demo of TurboPay. You can schedule an online demo here.
                  </AccordionContent>
                </AccordionItem> */}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12" data-testid="footer">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              {/* <a href="/" className="hover:text-[#1F497D] transition-colors">Home</a> */}
              <Link to="/" className="hover:text-[#1F497D] transition-colors">
                Home
              </Link>
              <Link to="/pricing" className="hover:text-[#1F497D] transition-colors">
                Pricing
              </Link>
              <Link to="/faq" className="hover:text-[#1F497D] transition-colors">
                FAQs
              </Link>
              {/* <a href="/faq" className="hover:text-[#1F497D] transition-colors">FAQs</a> */}
              <a href="#" className="hover:text-[#1F497D] transition-colors">Contact</a>
            </div>
            <p className="text-sm text-slate-400">© 2026 TurboPay. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};